import { ref, reactive, type Ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { compressImage } from '@/utils/imageHelper'
import { uploadImage } from '@/utils/imageService'

export interface IdCaptureState {
  showCropperDialog: boolean
  showUploadDialog: boolean
  cropperImage: string | null
  currentFile: File | null
  currentCropCallback: ((file: File) => void) | null
  uploadedImageUrl: string | null
  uploadedImagePreview: string | null
  isUploading: boolean
}

export function useIdCapture(externalCropperRef?: Ref<any>) {
  const toast = useToast()

  const state = reactive<IdCaptureState>({
    showCropperDialog: false,
    showUploadDialog: false,
    cropperImage: null,
    currentFile: null,
    currentCropCallback: null,
    uploadedImageUrl: null,
    uploadedImagePreview: null,
    isUploading: false,
  })

  const cropperRef = externalCropperRef || ref(null)

  // Open file selector and handle ID document selection
  function openIdUpload() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*,application/pdf'
    input.onchange = (event) => {
      const target = event.target as HTMLInputElement
      if (target.files && target.files[0]) {
        handleFileSelect(target.files[0])
      }
    }
    input.click()
  }

  // Handle file selection
  async function handleFileSelect(file: File) {
    console.log('🔍 COMPOSABLE: handleFileSelect called with file:', file)
    state.currentFile = file

    // Check if it's an image file that can be cropped
    if (file.type.startsWith('image/')) {
      console.log('🔍 COMPOSABLE: Image file detected, opening cropper dialog')
      // Open the cropper dialog
      openCropperDialog(file, async (croppedFile) => {
        try {
          await processImageFile(croppedFile)
        } catch (error) {
          console.error('Error processing ID document:', error)
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to process document',
            life: 3000,
          })
        }
      })
    } else {
      // For non-image files (like PDFs), use the original file
      await processImageFile(file)
    }
  }

  // Function to open the Cropper dialog
  function openCropperDialog(file: File, callback: (file: File) => void) {
    console.log('🔍 COMPOSABLE: openCropperDialog called')
    state.currentFile = file
    state.currentCropCallback = callback

    // Create a URL for the image
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        console.log('🔍 COMPOSABLE: FileReader loaded, setting cropper image')
        state.cropperImage = e.target.result as string
        state.showCropperDialog = true
        console.log('🔍 COMPOSABLE: showCropperDialog set to true')
      }
    }
    reader.readAsDataURL(file)
  }

  // Process the image file (compress and create preview)
  async function processImageFile(file: File) {
    try {
      state.isUploading = true

      // Compress the image if it's an image file
      let processedFile = file
      if (file.type.startsWith('image/')) {
        processedFile = await compressImage(file)
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          state.uploadedImagePreview = e.target.result as string
        }
      }
      reader.readAsDataURL(processedFile)

      // Store the processed file for later upload
      state.currentFile = processedFile
    } catch (error) {
      console.error('Error processing file:', error)
      throw error
    } finally {
      state.isUploading = false
    }
  }

  // Function to crop the image
  async function cropImage() {
    console.log('🔍 COMPOSABLE: cropImage called!')
    console.log('🔍 COMPOSABLE: cropperRef.value:', cropperRef.value)

    if (cropperRef.value) {
      console.log('🔍 COMPOSABLE: cropperRef.value exists!')
      try {
        const result = (cropperRef.value as any).getResult()
        console.log('🔍 COMPOSABLE: cropper result:', result)
        const { canvas } = result

        // Convert canvas to blob
        canvas.toBlob(
          async (blob: Blob | null) => {
            if (blob && state.currentFile) {
              // Create a File object from the blob
              const croppedFile = new File([blob], state.currentFile.name, {
                type: 'image/jpeg',
                lastModified: new Date().getTime(),
              })

              // Call the callback with the cropped file
              if (state.currentCropCallback) {
                state.currentCropCallback(croppedFile)
              }

              state.showCropperDialog = false
              state.cropperImage = null
            }
          },
          'image/jpeg',
          0.9,
        )
      } catch (error) {
        console.error('Error cropping image:', error)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to crop image',
          life: 3000,
        })
      }
    }
  }

  // Function to cancel cropping
  function cancelCrop() {
    state.showCropperDialog = false
    state.currentFile = null
    state.currentCropCallback = null
    state.cropperImage = null
  }

  // Function to rotate image in cropper
  function rotateImage(degrees: number) {
    if (cropperRef.value) {
      ;(cropperRef.value as any).rotate(degrees)
    }
  }

  // Upload the processed image
  async function uploadImageToStorage(path: string): Promise<string> {
    if (!state.currentFile) {
      throw new Error('No file to upload')
    }

    try {
      state.isUploading = true
      const url = await uploadImage(state.currentFile, path)
      state.uploadedImageUrl = url
      return url
    } catch (error) {
      console.error('Error uploading image:', error)
      throw error
    } finally {
      state.isUploading = false
    }
  }

  // Reset the state
  function reset() {
    state.showCropperDialog = false
    state.showUploadDialog = false
    state.cropperImage = null
    state.currentFile = null
    state.currentCropCallback = null
    state.uploadedImageUrl = null
    state.uploadedImagePreview = null
    state.isUploading = false
  }

  return {
    state,
    cropperRef,
    openIdUpload,
    handleFileSelect,
    openCropperDialog,
    processImageFile,
    cropImage,
    cancelCrop,
    rotateImage,
    uploadImage: uploadImageToStorage,
    reset,
  }
}
